/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { SsoAuthMethod } from '../../utils/loginSignupAnalyticsUtils';
import React from 'react';
import { Platform } from 'react-native';
import { TextViewProps } from '@airasia-phoenix/react-native/components/TextView';
import type { TFunction } from 'i18next';
import {
  ButtonViewProps,
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';

import { IconViewProps } from '@airasia-phoenix/react-native/components/IconView';
import { TextFieldProps } from '@airasia-phoenix/react-native/components/TextFieldView/types';
import { pd } from '@airasia-phoenix/react-native/utils';
import {
  getColor,
  getTextStyle,
  Orientation,
} from '@airasia-phoenix/react-native/utils/utils';
import {
  FieldType,
  TimeFieldProps,
} from '@airasia-phoenix/react-native/components/DateTimeFieldView';

import { MobileFieldViewProps } from '@airasia-phoenix/react-native/components/MobileFieldView';

import { RadioButtonGroupViewProps } from '@airasia-phoenix/react-native/components/RadioButtonGroupView';
import type { PhoneVerificationChannel } from '../../managers/sso';
import ArrowBack from '@move-travel/react-native-phoenix-icons/ArrowBack';
import ArrowBackIos from '@move-travel/react-native-phoenix-icons/ArrowBackIos';
import DateRange from '@move-travel/react-native-phoenix-icons/DateRange';

const ArrowBackPlatform = Platform.OS === 'ios' ? ArrowBackIos : ArrowBack;

export const GENDER_RADIO_HORIZONTAL_SPACING = pd.ref.space.spacing70.px / 2;

export type MobileLoginType = {
  mobileNumber: string;
  dialingCode: string;
};

export type SignUpFormProps = {
  onCloseIconClick?: () => void;
  headings: {
    closeIcon: IconViewProps;
    greeting: TextViewProps;
  };
  genderLabelProps: TextViewProps;
  genderErrorProps: TextViewProps;
  givenNameProps: TextFieldProps;
  surNameProps: TextFieldProps;
  mobileFieldProps: MobileFieldViewProps;
  isDialingCodeDisabled?: boolean;
  dobProps: TimeFieldProps;
  primaryButtonProps: ButtonViewProps;
  onSignUpSuccess?: () => void;
  onSignUpError?: (error: Error) => void;
  onLoadingChange?: (loading: boolean) => void;
  // Email and password from previous step
  email?: string;
  password?: string;
  isIncompleteSignup?: boolean;
  /** Sign-up method (google, emailOnly, …) used in analytics. */
  signupMethod?: SsoAuthMethod;
  onNavigateToVerifyOtp?: (data: {
    phoneNumber: string;
    dialingCode: string;
    availableChannels: PhoneVerificationChannel[];
  }) => void;
};

export const defaultProps: SignUpFormProps = {
  headings: {
    closeIcon: {
      height: 24,
      width: 24,
      tint: getColor(pd.sys.colour.content_primary),
      testID: 'close-icon',
      children: <ArrowBackPlatform />,
    },
    greeting: {
      text: 'Sign Up',
      style: getTextStyle(
        pd.sys.typescale.body_text_medium,
        pd.sys.colour.content_primary,
      ),
    },
  },
  genderLabelProps: {
    text: 'Gender',
    style: getTextStyle(
      pd.sys.typescale.sub_body_text,
      pd.sys.colour.content_primary,
    ),
    testID: 'gender-label',
  },
  genderErrorProps: {
    text: 'Please select your gender',
    style: getTextStyle(
      pd.sys.typescale.caption_text,
      pd.sys.colour.sentiment_high,
    ),
    testID: 'gender-error',
  },
  givenNameProps: {
    label: 'Given name',
    disabled: false,
    value: '',
    isError: false,
  },
  surNameProps: {
    label: 'Surname/ Family name',
    disabled: false,
    value: '',
    isError: false,
  },
  dobProps: {
    fieldType: FieldType.DATE_PICKER,
    secureTextEntry: false,
    disabled: false,
    label: 'Date of birth (DD/MM/YYYY)',
    endIcon: {
      height: 24,
      width: 24,
      tint: getColor(pd.sys.colour.content_secondary),
      children: <DateRange />,
    },
  },
  mobileFieldProps: {
    label: 'Mobile Number',
    disabled: false,
    value: '',
    isError: false,
    errorMessage: '',
    dialingCodeFieldViewProps: {
      label: 'Dial Code',
      value: '+60',
      disabled: false,
      isError: false,
      errorMessage: '',
      searchableListViewProps: {
        isInsideBottomSheetView: true,
        searchableListPageViewData: [],
        onChange: () => {},
        isTickList: false,
      },
    },
  },
  isDialingCodeDisabled: false,
  primaryButtonProps: {
    text: 'Continue',
    type: ButtonViewType.PRIMARY_REGULAR,
    disabled: false,
    testID: 'submit-button',
  },
};

export type Gender = 'male' | 'female';

export const getGenderOptions = (
  t: TFunction,
): { label: string; value: Gender }[] => [
  { label: t('signUpFormView.maleLabel'), value: 'male' },
  { label: t('signUpFormView.femaleLabel'), value: 'female' },
];

export type GenderRadioGroupDefaults = {
  orientation: RadioButtonGroupViewProps['orientation'];
  isMultiChoice: RadioButtonGroupViewProps['isMultiChoice'];
  viewHeight: RadioButtonGroupViewProps['viewHeight'];
};

export const defaultGenderRadioGroupProps: GenderRadioGroupDefaults = {
  orientation: new Orientation.Horizontal(
    2,
    1,
    GENDER_RADIO_HORIZONTAL_SPACING,
    0,
  ),
  isMultiChoice: false,
  viewHeight: 'auto',
};
